import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";

export default function PortalPagesTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "pages"), orderBy("order", "asc"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }));
      setRows(data);
    } catch (error) {
      console.error("Error loading pages:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleAddPage = async () => {
    try {
      const newPage = {
        key: "new-page",
        name: "New Page",
        description: "",
        icon: "📄",
        order: rows.length,
        enabled: true,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || "admin",
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "admin",
      };

      await addDoc(collection(db, "pages"), newPage);
      await loadPages();
    } catch (error) {
      console.error("Error adding page:", error);
    }
  };

  const handleDeletePage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this page?")) return;

    try {
      await deleteDoc(doc(db, "pages", id));
      await loadPages();
    } catch (error) {
      console.error("Error deleting page:", error);
    }
  };

  const processRowUpdate = async (newRow, oldRow) => {
    try {
      const updatePayload = {
        key: newRow.key,
        name: newRow.name,
        description: newRow.description || "",
        icon: newRow.icon || "",
        order: Number(newRow.order) || 0,
        enabled: Boolean(newRow.enabled),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "admin",
      };

      const docRef = doc(db, "pages", newRow.id);
      await updateDoc(docRef, updatePayload);

      const updatedRow = {
        ...oldRow,
        ...newRow,
      };

      setRows((prev) =>
        prev.map((row) => (row.id === newRow.id ? updatedRow : row))
      );

      return updatedRow;
    } catch (error) {
      console.error("Error updating page:", error);
      throw error;
    }
  };

  const columns = [
    { field: "key", headerName: "Key", width: 150, editable: true },
    { field: "name", headerName: "Name", width: 180, editable: true },
    { field: "description", headerName: "Description", width: 300, editable: true },
    { field: "icon", headerName: "Icon", width: 80, editable: true },
    {
      field: "order",
      headerName: "Order",
      width: 100,
      type: "number",
      editable: true,
    },
    {
      field: "enabled",
      headerName: "Enabled",
      width: 100,
      type: "boolean",
      editable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => handleDeletePage(params.row.id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", height: 700 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Portal Pages</Typography>
        <Button variant="contained" size="small" onClick={handleAddPage}>
          Add Page
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        editMode="row"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.error("Failed to save row update:", error);
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
      />
    </Box>
  );
}
